import { OptUtil } from "../../modules/s25-opt/s25.opt.util";
import type { Table } from "../../modules/s25-table/Table";
import { ListData } from "../../pojo/ListData";
import { PublisherService } from "../../services/publisher.service";
import type { ListCellValue, ListColumn, ListResponse } from "./list.table.types";
import type { ListType } from "./list.with.options.component";
import { ListConfig } from "./list.config";
import { PublisherDeleteCell } from "./publisher.delete.cell.component";
import { PublisherStatusCell } from "./publisher.status.cell.component";
import { PublisherSyncCell } from "./publisher.sync.cell.component";

export namespace PublisherListConfig {
    const RESULT_COUNT_SUFFIX = "Feeds";
    const LIST_COLUMN_PREF_NAME = "25L_publisher_admin_columns";

    export const admin: ListConfig = {
        caption: "Publisher Feeds",
        resultCountSuffix: RESULT_COUNT_SUFFIX,
        errorMessageLabel: "Publisher Feeds",
        listData: {
            compSubject: "publisher",
            listColumnPrefName: LIST_COLUMN_PREF_NAME,
            defaultSort: "object_name",
            defaultOrder: "asc",
        },
        loadOptBean: (refreshTable, _runTime) => OptUtil.formPublisherListBean(refreshTable, {}),
        optBeanRunTime: () => ({}),
        fetchListData,
        columnChooser: { listColumnPrefName: LIST_COLUMN_PREF_NAME },
        formatRowCount: ListConfig.defaultFormatRowCount(RESULT_COUNT_SUFFIX),
        cellMapping: {
            applyTableColumnDefaults(context, tableColumn, column) {
                tableColumn.width ??= "min-content";

                switch (context.getColumnId(column)) {
                    case "update":
                        tableColumn.cells = { component: PublisherSyncCell };
                        tableColumn.align = "center";
                        break;
                    case "delete":
                        tableColumn.cells = { component: PublisherDeleteCell };
                        tableColumn.align = "center";
                        break;
                    case "status":
                        tableColumn.cells = { component: PublisherStatusCell };
                        tableColumn.align = "center";
                        break;
                }

                if (column.prefname === "r25ws_query") {
                    tableColumn.maxWidth = 160;
                }

                if (column.prefname === "feed_uid") {
                    tableColumn.maxWidth = 160;
                }

                if (column.prefname === "send_date") {
                    tableColumn.minWidth = 100;
                }
            },

            mapCell(context, column, data, _formats) {
                return mapTemplateCell(context, context.getColumnId(column), data);
            },

            rowMapperOptions: {
                getId: rowId,
                getName: rowName,
            },
        },
    };

    export type AdminBean = {
        username: string;
        sortKey: string;
        sortOrder: string;
        searchKey: string;
        page_size: number;
        paginate: string;
        page: string;
    };

    type TemplateCellValue = {
        templateType: ListData.TemplateType;
        uid?: number;
        feedName?: string;
        calName?: string;
        status?: string;
    };

    type ListRow = { contextId?: string | number; row: readonly unknown[] };

    function rowId(columns: ListColumn[], row: ListRow): string | number {
        const uidColumnIndex = columns.findIndex((column) => column.prefname === "feed_uid");
        const uidCell = uidColumnIndex >= 0 ? row.row[uidColumnIndex] : undefined;
        return typeof uidCell === "string" || typeof uidCell === "number"
            ? uidCell
            : (row.contextId ?? rowName(columns, row));
    }

    function rowName(columns: ListColumn[], row: ListRow): string {
        const nameColumnIndex = columns.findIndex((column) => column.prefname === "object_name");
        const nameCell = nameColumnIndex >= 0 ? row.row[nameColumnIndex] : undefined;
        return typeof nameCell === "string" ? nameCell : String(row.contextId ?? "");
    }

    function mapTemplateCell(
        context: ListConfig.CellMappingContext,
        columnId: string,
        data: ListCellValue | TemplateCellValue,
    ): [string, Table.Cell] | undefined {
        if (typeof data !== "object" || !data || !("templateType" in data)) {
            return undefined;
        }

        const templateData = data as TemplateCellValue;

        switch (templateData.templateType) {
            case ListData.TemplateType.PublisherUpdate:
                return [
                    columnId,
                    {
                        inputs: {
                            uid: templateData.uid,
                        },
                        outputs: {
                            refreshed: () => context.refreshTable(),
                        },
                    },
                ];
            case ListData.TemplateType.PublisherDelete:
                return [
                    columnId,
                    {
                        inputs: {
                            uid: templateData.uid,
                            feedName: templateData.feedName ?? "",
                            calName: templateData.calName ?? "",
                        },
                        outputs: {
                            refreshed: () => context.refreshTable(),
                        },
                    },
                ];
            case ListData.TemplateType.PublisherStatus:
                return [columnId, { inputs: { status: templateData.status ?? "" } }];
            default:
                return undefined;
        }
    }

    async function fetchListData(context: ListConfig.FetchContext): Promise<ListResponse> {
        const { config, listType, tableQuery } = context;
        if (listType.kind !== "publisherAdmin") {
            throw new Error("Publisher fetch requires publisherAdmin list type");
        }

        const listData = config.listData;
        if (!listData) {
            throw new Error(`List data config missing for ${config.caption}`);
        }

        const { sortColumn, sortOrder } = ListConfig.extractTableSort(tableQuery);

        const params = {
            modelBean: {
                publisherBean: listType.publisherBean,
                compsubject: listData.compSubject,
                listColumnPrefName: listData.listColumnPrefName,
                sortCol: { sort: sortColumn, order: sortOrder },
                chosen: { page: tableQuery.page + 1 },
                itemsPerPage: tableQuery.pageSize,
                cacheId: tableQuery.forceRefresh ? 0 : tableQuery.cacheId,
                paginationCall: !tableQuery.forceRefresh && tableQuery.cacheId ? 1 : undefined,
                defaultSort: listData.defaultSort,
                defaultOrder: listData.defaultOrder,
                resultCountSuffix: config.resultCountSuffix,
                caption: config.caption,
            },
        };

        return (await PublisherService.getPublisherList({
            ...params,
            abortSignal: context.abortSignal,
        })) as ListResponse;
    }

    export function forType(listType: ListType): ListConfig {
        if (listType.kind !== "publisherAdmin") {
            throw new Error(`Unsupported publisher admin list type kind ${listType.kind}`);
        }
        return admin;
    }
}
